import { useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { PracticeSchedule } from "@astrana/ui/modules";
import { GraphQLProvider } from "@astrana/ui/provider";

import { useAuthContext } from "../auth/AuthContext";
import { useCurrentUser } from "../auth/CurrentUserContext";
import PpLoginModal from "../auth/PpLoginModal";
import { useSessionEvents } from "../auth/useSessionEvents";
import { featureFlags } from "../config/featureFlags";
import { clinicalSummaryTitleOverride } from "../members/clinicalSummaryTitleOverride";
import { useDarkMode } from "../theme/useDarkMode";
import { GraphQLClientGate } from "../lib/apollo/GraphQLClientGate";
import {
  ScheduleResolvingState,
  SchedulePpLoginRequiredState,
} from "./ScheduleGateStates";
import { useScheduleAccessGate } from "./useScheduleAccessGate";

// The care-management-gql endpoint the `practiceSchedule` resolver lives on —
// the module fetches through @astrana/ui's Apollo singleton, configured below.
const GRAPHQL_URL = import.meta.env.VITE_CARE_MANAGEMENT_GQL_ENDPOINT ?? "";

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Thin host for @astrana/ui's native `PracticeSchedule` module. All schedule
 * logic/UI now lives in the library (mirrors how `WorklistPage` hosts the
 * native Patient Priority Index module); this file owns only member-360
 * concerns:
 *
 *  - URL state — date + providerNpi live in the query string via
 *    `useSearchParams`, passed to the module as CONTROLLED props so the
 *    schedule stays deep-linkable and browser-back (after opening a member)
 *    returns to the SAME day/provider. react-router stays out of the library.
 *  - Navigation — `onOpenMember` routes to the member profile, carrying the
 *    current schedule URL as `state.from` so the profile's back button returns
 *    here precisely.
 *  - GraphQL/auth — wraps the module in `<GraphQLProvider>` + `<GraphQLClientGate>`
 *    exactly like `MemberProfilePage`, so the module's `useAstranaUIQuery`
 *    singleton is configured with the care-mgmt-gql endpoint + live bearer.
 *    (WorklistPage doesn't need this because PPI uses its own RTK store, not
 *    the Apollo singleton.) The gate + token-derived `key` dodge the
 *    first-render singleton-capture race and force a remount on token refresh.
 *  - Theme + error toasts.
 */
export function PracticeSchedulePage({ initialDate }: { initialDate?: string } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, accountLabel, selectedProvider } = useAuthContext();
  // AM-5828 — see MemberProfilePage: identity, not the token's value, is what
  // may drop the Apollo cache.
  const apolloIdentityKey = `${selectedProvider ?? "none"}:${accountLabel ?? "anon"}`;
  const { triggerExpiryWarning } = useSessionEvents();
  const { isDark } = useDarkMode();
  // Header title for each row's Clinical Summary card (ACT-1239 Phase 3,
  // gated off by ACT-1488 / T9). UNDEFINED BY DEFAULT: with
  // `personaClinicalSummaryTitle` off, PracticeSchedule gets no `summaryTitle`
  // and each row's card renders @astrana/ui's literal "Clinical Summary",
  // matching Atlas/PP and — importantly — matching the Home tab, which shares
  // this resolver. Read defensively either way: `raw` is null while the
  // connectCurrentUser query is loading/erroring.
  const { raw: currentUserRaw } = useCurrentUser();
  const summaryTitle = clinicalSummaryTitleOverride(
    currentUserRaw?.currentPersona?.clinicalSummaryTemplate?.title,
    featureFlags.personaClinicalSummaryTitle,
  );

  // PP-login gate — the `practiceSchedule` resolver is PP-scoped (it scopes
  // providers/appointments to the NPIs the signed-in Provider Portal identity
  // can access), so a live PP bearer is required before the module renders.
  // Simpler than the worklist's gate: no NPI entry, no IPA picker — just the
  // bearer. See useScheduleAccessGate.ts.
  const accessGate = useScheduleAccessGate();

  const date = searchParams.get("date") ?? initialDate ?? todayIso();
  const providerNpi = searchParams.get("providerNpi") ?? "";

  const patchParams = (mut: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mut(next);
    setSearchParams(next, { replace: true });
  };
  const setDate = (d: string) => patchParams((p) => (d ? p.set("date", d) : p.delete("date")));
  const setProviderNpi = (npi: string) =>
    patchParams((p) => (npi ? p.set("providerNpi", npi) : p.delete("providerNpi")));

  const openMember = (id: string) =>
    navigate(`/members/${id}`, { state: { from: location.pathname + location.search } });

  // Same auth-failure signal MemberProfilePage uses, via a stable ref so the
  // provider's `onAuthFailure` prop identity doesn't churn on every render.
  const authFailureRef = useRef<() => void>(() => {});
  authFailureRef.current = () => triggerExpiryWarning("auth-failure");
  const onAuthFailure = useRef(() => authFailureRef.current()).current;

  // Gate: no PP bearer (and none silently refreshable) → login-required CTA;
  // a persisted PP link mid silent-refresh → a lightweight spinner. Neither
  // renders the module. See useScheduleAccessGate.ts's status contract.
  if (accessGate.status !== "ready") {
    return (
      <div className="h-full min-h-0 flex flex-col">
        {accessGate.status === "resolving" ? (
          <ScheduleResolvingState />
        ) : (
          <SchedulePpLoginRequiredState
            identityEmail={accessGate.identityEmail}
            onLogin={accessGate.onLogin}
          />
        )}
        {accessGate.loginOpen && accessGate.identityEmail && (
          <PpLoginModal
            email={accessGate.identityEmail}
            onSuccess={accessGate.onLoginSuccess}
            onCancel={accessGate.onLoginCancel}
          />
        )}
      </div>
    );
  }

  return (
    <GraphQLProvider
      clientUrl={GRAPHQL_URL}
      authToken={accessToken ?? undefined}
      ppBearer={accessGate.ppBearer}
      onAuthFailure={onAuthFailure}
      identityKey={apolloIdentityKey}
    >
      {/* AM-5828 — sign-in state, not the token value: rotation no longer
          needs a remount (the bearer is read live per request). */}
      <GraphQLClientGate key={accessToken ? "auth" : "anon"}>
        <PracticeSchedule
          date={date}
          onDateChange={setDate}
          providerNpi={providerNpi}
          onProviderChange={setProviderNpi}
          onOpenMember={openMember}
          onError={() => toast.error("Couldn't load the schedule.")}
          theme={isDark ? "dark" : "light"}
          summaryTitle={summaryTitle}
        />
      </GraphQLClientGate>
    </GraphQLProvider>
  );
}

export default PracticeSchedulePage;
